import { RefObject, useLayoutEffect, useMemo } from 'react';

import { HighchartsReact } from 'highcharts-react-official';

import { useAutoResizeHighChart } from './hooks.ts';

type AutoResizeHighChartProps = Omit<
  HighchartsReact.Props,
  'options' | 'containerProps' | 'ref'
> & {
  options: NonNullable<HighchartsReact.Props['options']>;
  highChartsRef: RefObject<HighchartsReact.RefObject | null>;
  containerProps?: HighchartsReact.Props['containerProps'];
};

export const AutoResizeHighChart = ({
  options,
  highChartsRef,
  containerProps,
  ...highchartsProps
}: AutoResizeHighChartProps) => {
  useAutoResizeHighChart(highChartsRef);

  const optionsWithAutoHeight = useMemo<NonNullable<HighchartsReact.Props['options']>>(
    () => ({
      ...options,
      chart: {
        ...options.chart,
        height: null,
      },
    }),
    [options]
  );

  // `height: null` makes every options update size the chart from its content, not its box.
  // HighchartsReact applies the update in a child layout effect, so re-fit right after it.
  useLayoutEffect(() => {
    const chart = highChartsRef.current?.chart;
    const container = chart?.container?.parentElement;
    if (chart && container) {
      chart.setSize(undefined, container.clientHeight, false);
    }
  }, [highChartsRef, /* effect dep */ optionsWithAutoHeight]);

  const mergedContainerProps = useMemo<HighchartsReact.Props['containerProps']>(
    () => ({
      ...containerProps,
      style: {
        ...containerProps?.style,
        height: '100%',
      },
    }),
    [containerProps]
  );

  return (
    <HighchartsReact
      {...highchartsProps}
      ref={highChartsRef}
      options={optionsWithAutoHeight}
      containerProps={mergedContainerProps}
    />
  );
};
